import type { ContentListRevisionsOutput } from '@vivport/backend/types';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { SUBTYPE_META } from '@/components/provenance/ProvenanceTouchList';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useRestoreRevision, useRevisionList, useRevisionSnapshot } from '@/hooks/useRevisionQueries';
import { cn } from '@/lib/utils';
import { renderMarkdown } from '@/pages/details/document-markdown';

type RevisionItem = ContentListRevisionsOutput['items'][number];

interface RevisionsPanelProps {
  contentId: number;
  code: string;
  title: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const RELATIVE_UNITS: Array<{ unit: Intl.RelativeTimeFormatUnit; ms: number }> = [
  { unit: 'year', ms: 365 * 24 * 60 * 60 * 1000 },
  { unit: 'month', ms: 30 * 24 * 60 * 60 * 1000 },
  { unit: 'day', ms: 24 * 60 * 60 * 1000 },
  { unit: 'hour', ms: 60 * 60 * 1000 },
  { unit: 'minute', ms: 60 * 1000 },
];

function formatRelativeTime(iso: string): string {
  const diff = new Date(iso).getTime() - Date.now();
  if (!Number.isFinite(diff)) return '';
  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });
  for (const { unit, ms } of RELATIVE_UNITS) {
    if (Math.abs(diff) >= ms) return formatter.format(Math.round(diff / ms), unit);
  }
  return 'just now';
}

function formatBytes(bytes: number): string {
  const abs = Math.abs(bytes);
  if (abs < 1024) return `${bytes} B`;
  return `${(bytes / 1024).toFixed(1)} KB`;
}

function formatSizeDelta(item: RevisionItem, older: RevisionItem | undefined): string {
  if (older === undefined) return formatBytes(item.bodyBytes);
  const delta = item.bodyBytes - older.bodyBytes;
  if (delta === 0) return 'no change';
  return `${delta > 0 ? '+' : '−'}${formatBytes(Math.abs(delta))}`;
}

export function RevisionsPanel({
  contentId,
  code,
  title,
  open,
  onOpenChange,
}: RevisionsPanelProps) {
  const listQuery = useRevisionList(open ? contentId : null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const snapshotQuery = useRevisionSnapshot(open ? selectedId : null);
  const restoreMutation = useRestoreRevision();

  useEffect(() => {
    if (!open) {
      setSelectedId(null);
      setConfirmOpen(false);
    }
  }, [open]);

  const items = listQuery.data?.items ?? [];
  const total = items.length;
  const selected = items.find((item) => item.contentId === selectedId) ?? null;
  const snapshot = snapshotQuery.data;

  const handleRestore = async () => {
    if (selected === null) return;
    try {
      const result = await restoreMutation.mutateAsync({ contentId: selected.contentId, code });
      toast.success(
        `Restored revision ${result.restoredFrom.revision}; this is now revision ${result.head.revision}.`
      );
      setConfirmOpen(false);
      onOpenChange(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : String(error));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Revisions</DialogTitle>
          <DialogDescription>{title.length > 0 ? title : code}</DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {listQuery.isPending ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading history…
            </div>
          ) : listQuery.isError ? (
            <p className="text-sm text-destructive">{listQuery.error.message}</p>
          ) : (
            <ul className="divide-y rounded-md border" data-testid="revision-list">
              {items.map((item, index) => {
                const older = items[index + 1];
                const meta = item.apex ? SUBTYPE_META[item.apex.kind] : null;
                const isSelected = item.contentId === selectedId;
                return (
                  <li key={item.contentId}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(item.isHead ? null : item.contentId)}
                      className={cn(
                        'flex w-full items-center gap-3 px-3 py-2 text-left text-sm transition-colors hover:bg-accent',
                        isSelected && 'bg-accent'
                      )}
                      data-testid={`revision-row-${item.revision}`}
                    >
                      <span className="w-20 shrink-0 font-medium tabular-nums">Revision {item.revision}</span>
                      <span className="min-w-0 flex-1">
                        <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                          {meta ? (
                            <span className="inline-flex items-center gap-1 text-muted-foreground">
                              <meta.Icon className="h-3.5 w-3.5" />
                              {meta.label}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">No session</span>
                          )}
                          {item.apex ? <span className="truncate">{item.apex.label}</span> : null}
                          <span className="text-muted-foreground">{item.apex?.memberName ?? 'Unknown'}</span>
                        </span>
                        <span className="flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
                          <span title={new Date(item.at).toLocaleString()}>{formatRelativeTime(item.at)}</span>
                          <span>{formatSizeDelta(item, older)}</span>
                          {item.titleChanged ? <span>title changed</span> : null}
                        </span>
                      </span>
                      {item.isHead ? <Badge variant="outline">Current</Badge> : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {selected !== null ? (
            <div className="space-y-3 rounded-md border p-3" data-testid="revision-snapshot">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-medium">
                  Viewing revision {selected.revision} of {total} — read-only
                </p>
                <Button size="sm" onClick={() => setConfirmOpen(true)} disabled={snapshotQuery.isPending}>
                  Restore
                </Button>
              </div>
              {snapshotQuery.isPending ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading snapshot…
                </div>
              ) : snapshotQuery.isError ? (
                <p className="text-sm text-destructive">{snapshotQuery.error.message}</p>
              ) : snapshot ? (
                <div className="max-h-[50vh] overflow-y-auto">
                  {snapshot.title !== null && snapshot.title.length > 0 ? (
                    <p className="mb-2 font-medium">{snapshot.title}</p>
                  ) : null}
                  {snapshot.contentType === 'DOCUMENT' ? (
                    <div className="prose prose-sm max-w-none dark:prose-invert">
                      {renderMarkdown(snapshot.description ?? '')}
                    </div>
                  ) : (
                    <p className="whitespace-pre-wrap text-sm">{snapshot.description ?? ''}</p>
                  )}
                </div>
              ) : null}
            </div>
          ) : null}

        </DialogBody>

        <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Restore revision {selected?.revision}?</DialogTitle>
              <DialogDescription>
                This creates a new revision with this content. Nothing in the history is changed or deleted.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={restoreMutation.isPending}>
                Cancel
              </Button>
              <Button onClick={() => void handleRestore()} disabled={restoreMutation.isPending}>
                {restoreMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Restore
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}
